import React, { useState } from 'react';
import { styled } from 'linaria/react';
import t, { useIntl } from '@jetshop/intl';
import { theme } from '../Theme';
import UIButton from '../ui/Button';
import { ReactComponent as ShareIcon } from '../../svg/Share.svg';

const ShareButton = styled(UIButton)`
  font-size: 12px;
  margin-top: 0.5rem;
  outline: none;
  text-align: center;
  transition: all ease 0.3s;
  width: 100%;
  font-weight: bold;
  margin-bottom: 0.5rem;
  background: ${theme.colors.primary};
  color: white;
  height: 42px;
  line-height: 42px;
  font-size: 14px;
  border: 2px solid ${theme.colors.primary};
  &:hover {
    background: white;
    color: ${theme.colors.primary};
  }
`;

const IconShareButton = styled.button`
  background: none;
  border: none;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: ${theme.colors.primary};
  
  svg {
    width: 20px;
    height: 20px;
    stroke: currentColor;
  }
  
  &:hover {
    color: ${theme.colors.accent};
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const CopiedMessage = styled.div`
  text-align: center;
  color: ${theme.colors.accent};
  font-size: 12px;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  font-weight: bold;
`;

const CopiedTooltip = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 0.5rem;
  background: ${theme.colors.accent};
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: bold;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  
  &:before {
    content: '';
    position: absolute;
    bottom: 100%;
    right: 1rem;
    border: 6px solid transparent;
    border-bottom-color: ${theme.colors.accent};
  }
`;

const TextShareLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${theme.colors.primary};
  font-size: 0.875rem;
  text-decoration: underline;
  transition: opacity 0.2s ease;
  font-family: ${theme.fonts.body};
  
  &:hover {
    opacity: 0.7;
  }
  
  &:active {
    opacity: 0.5;
  }
`;

const TextLinkWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

/**
 * Encodes cart items into a shareable URL
 * Uses compact array format to minimize URL length:
 * [articleNumber, quantity, variantArticleNumber (optional)]
 * @param {Array} items - Cart items from the cart query
 * @returns {string} - Shareable URL
 */
export const createShareableCartUrl = items => {
  // Safety check
  if (!items || !Array.isArray(items) || items.length === 0) {
    console.warn('createShareableCartUrl: No items to share');
    return null;
  }

  // Use compact array format: [articleNumber, quantity, variantArticleNumber?]
  // This saves ~60% compared to object format with property names
  const cartData = items.map(item => {
    const arr = [
      item.articleNumber,
      item.quantity
    ];
    
    // Only add variant article number if it exists and differs from main
    if (item.variant && item.variant.articleNumber && 
        item.variant.articleNumber !== item.articleNumber) {
      arr.push(item.variant.articleNumber);
    }
    
    return arr;
  });

  // Encode to base64 for URL
  const encodedData = btoa(JSON.stringify(cartData));
  
  // Create full URL
  const baseUrl = window.location.origin;
  const shareUrl = `${baseUrl}/shared-cart?data=${encodedData}`;
  
  return shareUrl;
};

/**
 * Decodes the shared cart URL parameter
 * Supports both compact array format and legacy object format
 * @param {string} encodedData - The encoded cart data from URL
 * @returns {Array} - Array of cart items in object format
 */
export const decodeSharedCartData = encodedData => {
  // Safety check: only run in browser environment
  if (typeof window === 'undefined') {
    return [];
  }
  
  // Validate input
  if (!encodedData || typeof encodedData !== 'string' || encodedData.trim() === '') {
    return [];
  }
  
  try {
    // Clean the encoded data - remove any trailing garbage like "[object Object]"
    // Base64 strings only contain A-Z, a-z, 0-9, +, /, and = for padding
    let cleanedData = encodedData.trim();
    
    // If there's a space or other invalid character, truncate there
    const invalidCharMatch = cleanedData.match(/^([A-Za-z0-9+/=]+)/);
    if (invalidCharMatch) {
      cleanedData = invalidCharMatch[1];
    }
    
    const decodedString = atob(cleanedData);
    const parsed = JSON.parse(decodedString);
    
    // Ensure we return an array
    if (!Array.isArray(parsed)) {
      return [];
    }
    
    // Convert compact array format to object format if needed
    // Array format: [articleNumber, quantity, variantArticleNumber?]
    // Object format: { articleNumber, quantity, variantArticleNumber? }
    return parsed.map(item => {
      // If already in object format, return as is
      if (typeof item === 'object' && !Array.isArray(item)) {
        return item;
      }
      
      // Convert from array format
      if (Array.isArray(item)) {
        const result = {
          articleNumber: item[0],
          quantity: item[1]
        };
        
        // Add variant article number if present
        if (item[2]) {
          result.variantArticleNumber = item[2];
        }
        
        return result;
      }
      
      // Invalid format, skip
      return null;
    }).filter(item => item !== null);
    
  } catch (error) {
    if (typeof console !== 'undefined') {
      console.error('Failed to decode shared cart data:', error);
    }
    return [];
  }
};

/**
 * ShareCart Button Component
 * Generates a shareable link for the current cart
 * @param {Array} items - Cart items
 * @param {boolean} iconOnly - If true, renders as icon button only
 * @param {boolean} textLink - If true, renders as underlined text link
 */
const ShareCart = ({ items, iconOnly = false, textLink = false }) => {
  const [copied, setCopied] = useState(false);
  const translate = useIntl();

  const handleShare = async () => {
    const shareUrl = createShareableCartUrl(items);

    // Check if URL was created successfully
    if (!shareUrl) {
      console.error('Failed to create share URL');
      return;
    }

    // Try to use native share API if available (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: translate('Check out my cart'),
          text: translate('Check out these products I found'),
          url: shareUrl
        });
        return;
      } catch (err) {
        // If share fails or is cancelled, fall back to clipboard
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    }

    // Fallback to clipboard
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Final fallback: create a text input and select it
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      } catch (err2) {
        console.error('Fallback copy failed:', err2);
      }
      document.body.removeChild(textArea);
    }
  };

  if (!items || items.length === 0) {
    return null;
  }

  if (iconOnly) {
    return (
      <div style={{ position: 'relative' }}>
        <IconShareButton 
          onClick={handleShare}
          aria-label={translate('Share cart')}
          title={translate('Share cart')}
        >
          <ShareIcon />
        </IconShareButton>
        {copied && <CopiedTooltip>{t('Link copied!')}</CopiedTooltip>}
      </div>
    );
  }

  if (textLink) {
    return (
      <TextLinkWrapper>
        <TextShareLink 
          onClick={handleShare}
          aria-label={translate('Share cart')}
        >
          {t('Share cart')}
        </TextShareLink>
        {copied && <CopiedTooltip>{t('Link copied!')}</CopiedTooltip>}
      </TextLinkWrapper>
    );
  }

  return (
    <>
      <ShareButton onClick={handleShare}>
        {t('Share cart')}
      </ShareButton>
      {copied && <CopiedMessage>{t('Link copied to clipboard!')}</CopiedMessage>}
    </>
  );
};

export default ShareCart;

